import { CompanyAPI } from '@/api/backend-client'
import { UserAPI } from '@/api/backend-client'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'
import { UserType } from '@/api/types'
import { Referral } from '@/api/model/referral'

export const getAllowedUsers = async (
    referredByBranch: Branch|null,
    referredByUser: User|null,
    assignedToBranch: Branch|null,
    assignedToUser: User|null,
): Promise<User[]> => {
    const users = new Map<string, User>()

    // In parallel; a company or user that can't be fetched adds nobody (the given user still counts for the checks)
    const [referredByCompany, fetchedReferredBy, assignedToCompany, fetchedAssignedTo] = await Promise.all([
        referredByBranch ? CompanyAPI.get(referredByBranch.company.id) : null,
        referredByUser ? UserAPI.get(referredByUser.id) : null,
        assignedToBranch ? CompanyAPI.get(assignedToBranch.company.id) : null,
        assignedToUser ? UserAPI.get(assignedToUser.id) : null,
    ])

    referredByCompany?.users.forEach((user: User) => users.set(user.id, user))

    if (fetchedReferredBy) {
        referredByUser = fetchedReferredBy
        users.set(referredByUser.id, referredByUser)
    }

    assignedToCompany?.users.forEach((user: User) => users.set(user.id, user))

    if (fetchedAssignedTo) {
        assignedToUser = fetchedAssignedTo
        users.set(assignedToUser.id, assignedToUser)
    }

    return Array.from(users.values())
        .filter((user: User) => {
            if (user.type !== UserType.DEFAULT) {
                return false
            }

            return isAllowedUser(user, referredByBranch, referredByUser, assignedToBranch, assignedToUser)
        })

        .sort((a: User, b: User) => a.name.localeCompare(b.name))
}

export const isAllowedUser = (
    user: User,
    referredByBranch: Branch|null,
    referredByUser: User|null,
    assignedToBranch: Branch|null,
    assignedToUser: User|null,
): boolean => {
    if (user.isCompanyManager) {
        // For company managers, the user must be in the referred by or assigned to company
        if (referredByBranch && referredByBranch.company && referredByBranch.company.id === user.company.id) {
            return true
        }

        if (referredByUser && referredByUser.company && referredByUser.company.id === user.company.id) {
            return true
        }

        if (assignedToBranch && assignedToBranch.company && assignedToBranch.company.id === user.company.id) {
            return true
        }

        if (assignedToUser && assignedToUser.company && assignedToUser.company.id === user.company.id) {
            return true
        }

        return false
    } else if (user.isBranchManager || user.isTeamLead) {
        // For branch managers and team leads, the user must be in the referred by or assigned to branch
        if (referredByBranch && user.branches.some((branch: Branch) => referredByBranch.id === branch.id)) {
            return true
        }

        if (assignedToBranch && user.branches.some((branch: Branch) => assignedToBranch.id === branch.id)) {
            return true
        }

        return false
    } else {
        // For employees, the user must be the referred by or assigned to user
        if (referredByUser && referredByUser.id === user.id) {
            return true
        }

        if (assignedToUser && assignedToUser.id === user.id) {
            return true
        }

        return false
    }
}

export const getAllowedUsersForReferral = async (
    referral: Referral,
): Promise<User[]> => {
    return getAllowedUsers(
        referral.referredByBranch,
        referral.referredBy,
        referral.assignedToBranch,
        referral.assignedTo,
    )
}

export const isAllowedUserForReferral = (
    user: User,
    referral: Referral,
): boolean => {
    // Check default permissions
    const isAllowed = isAllowedUser(
        user,
        referral.referredByBranch,
        referral.referredBy,
        referral.assignedToBranch,
        referral.assignedTo,
    )

    if (isAllowed) {
        return true
    }

    // Check additional permissions (if any)
    if (referral.permissions) {
        // Check additional user permissions
        if (referral.permissions.users.includes(user.id)) {
            return true
        }

        // Check additional branch permissions
        if (user.branches.some(branch => referral.permissions.branches.includes(branch.id))) {
            return true
        }
    }

    // Not allowed
    return false
}
